"use client";
import styles from "./Navbar.module.scss";
import type { MenuType } from "../../types/baseTypes";
import type { NavigationOptionsType } from "@/types/ModuleOptionsType";
import type { OptionsNavigationType } from "@/types/OptionType";
import { Menu } from "./Menu/Menu";
import { LanguageToggle } from "./Language/LanguageToggle";
import { Hamburger } from "./Hamburger/Hamburger";
import { useState } from "react";
import { useAppContext } from "../../context/AppContext";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import type { Post } from "@aleksundshantu/gp-types/client";
import type { MegaMenuHighlightsType } from "./Menu/MegaMenu/MegaMenu";
import useScrollLock from "@/hooks/useScrollLock";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useScrolledAttribute } from "@/hooks/useScrolledAttribute";

interface NavbarProps {
  menu: MenuType;
  navbarOption: NavigationOptionsType;
  themeNavigation?: OptionsNavigationType;
  megaMenuHighlights: MegaMenuHighlightsType;
  pageData?: Post;
  isHome: boolean;
  logos: JSX.Element;
  topMenu: JSX.Element | null;
  topMenuData: MenuType | null;
  search: JSX.Element | false;
  socialMedia: JSX.Element | false;
}

export const NavbarClient = ({
  navbarOption,
  themeNavigation,
  megaMenuHighlights,
  menu,
  pageData,
  isHome,
  logos,
  topMenu,
  topMenuData,
  search,
  socialMedia,
}: NavbarProps) => {
  const { show_box_shadow, show_search, show_language } =
    navbarOption.header_menu;
  const { openMenu } = useAppContext();
  const navRef = useScrolledAttribute<HTMLElement>();
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const isNavbarBreakpoint = useMediaQuery(
    `(min-width: ${styles.navbarBreakpoint})`,
  );

  useScrollLock(openMenu || (isNavbarBreakpoint && isMegaMenuOpen));

  return (
    <nav
      ref={navRef}
      data-home={isHome}
      data-scrolled={false}
      data-show-shadow={show_box_shadow}
      //! please dont remove scrollbar-width class
      className={`${styles["navbar"]} ${openMenu ? styles["opened"] : ""} navbar scrollbar-width`}
    >
      {topMenu}
      <div className={styles["navbar-shell"]}>
        {logos}
        <Hamburger />
        <FocusTrap disabled={!openMenu}>
          <div
            id="navbar-main"
            data-show-search={show_search}
            data-lenis-prevent
            className={`${styles["navbar-main"]} ${openMenu ? styles["opened"] : ""}`}
          >
            <div className={styles["navbar-main-mobile-scroll"]}>
              <Menu
                menu={menu}
                pageData={pageData}
                themeNavigation={themeNavigation}
                megaMenuHighlights={megaMenuHighlights}
                topMenu={topMenuData}
                onMegaMenuOpenChange={setIsMegaMenuOpen}
              />
              {socialMedia}
              {show_language && pageData && (
                <LanguageToggle addWrapper={true} post={pageData} />
              )}
              {search}
            </div>
          </div>
        </FocusTrap>
      </div>
    </nav>
  );
};
